/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faCalendar, 
  faUser, 
  faClock,
  faTag,
  faHome,
  faBook,
  faHeart,
  faComment,
  faTimes,
  faPaperPlane,
  faShare
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { 
  fetchPublicBlog, 
  toggleBlogLike, 
  getBlogLikes, 
  addBlogComment, 
  getBlogComments 
} from '../api/blogApi';
import { buildApiUrl } from '../config/api';
import '../styles/Blog.css';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [comments, setComments] = useState(0);
  const [commentList, setCommentList] = useState([]);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    const loadBlog = async () => {
      setLoading(true);
      setError(null);
      try {
        const blogData = await fetchPublicBlog(id);
        if (blogData) {
          setBlog(blogData);
        } else {
          setError('Blog post not found');
        }
      } catch (err) {
        console.error('Failed to load blog:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadBlog();
    }
  }, [id]);

  useEffect(() => {
    const loadEngagement = async () => {
      if (!id) return;
      
      try {
        const userId = user?.id || user?._id || null;
        // Load likes
        const likesData = await getBlogLikes(id, userId);
        setLikes(likesData.likes || 0);
        setIsLiked(likesData.isLiked || false);
        
        // Load comments
        const commentsData = await getBlogComments(id);
        setCommentList(commentsData.comments || []);
        setComments(commentsData.commentCount || 0);
      } catch (err) {
        console.error('Failed to load engagement data:', err);
      }
    };

    if (id && !loading) {
      loadEngagement();
    }
  }, [id, user, loading]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareMenu && !event.target.closest('.blog-share-wrapper')) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareMenu]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80';
    if (/^https?:\/\//i.test(imagePath)) {
      return imagePath;
    }
    const normalized = imagePath.startsWith('/') ? imagePath.replace(/^\/+/, '') : imagePath;
    if (normalized.startsWith('uploads/')) {
      const uploadPath = normalized.replace(/^uploads\//, '');
      return buildApiUrl(`/api/users/uploads/${uploadPath}`);
    }
    return buildApiUrl(imagePath.startsWith('/') ? imagePath : `/${imagePath}`);
  };

  const handleLike = async () => {
    if (!user) {
      // Optionally redirect to login or show message
      alert('Please login to like posts');
      return;
    }

    const userId = user.id || user._id;
    if (!userId) return;

    try {
      const result = await toggleBlogLike(id, userId);
      if (result) {
        setLikes(result.likes);
        setIsLiked(result.isLiked);
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
      // Revert optimistic update
      setIsLiked(!isLiked);
      setLikes(prev => isLiked ? prev + 1 : prev - 1);
    }
  };

  const handleComment = () => {
    if (!user) {
      alert('Please login to comment');
      return;
    }
    setShowCommentDialog(true);
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) {
      alert('Please enter a comment');
      return;
    }

    if (!user) return;

    const userId = user.id || user._id;
    const userName = user.name || user.firstName || 'Anonymous';

    setSubmittingComment(true);
    try {
      const result = await addBlogComment(id, userId, userName, commentText);
      if (result) {
        setCommentList(result.comments || []);
        setComments(result.commentCount || 0);
        setCommentText('');
        setShowCommentDialog(false);
      }
    } catch (err) {
      console.error('Failed to add comment:', err);
      alert('Failed to add comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatCommentDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getShareUrl = () => {
    return window.location.href;
  };

  // Detect if user is on mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  };

  const cleanHtml = (html) => {
    if (!html) return '';
    
    // First, remove all script and style tags with their content
    let cleaned = html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    cleaned = cleaned.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    
    // Remove all HTML tags
    cleaned = cleaned.replace(/<[^>]+>/g, '');
    
    // Decode HTML entities
    const tmp = document.createElement('div');
    tmp.innerHTML = cleaned;
    let text = tmp.textContent || tmp.innerText || '';
    
    // Clean up extra whitespace and newlines
    text = text.replace(/\s+/g, ' ').trim();
    text = text.replace(/\n\s*\n/g, '\n\n'); // Clean up multiple newlines
    
    // Remove any remaining HTML entity patterns
    text = text.replace(/&[a-z#][a-z0-9]*;/gi, (entity) => {
      const entities = {
        '&nbsp;': ' ',
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"',
        '&#39;': "'",
        '&apos;': "'"
      };
      return entities[entity.toLowerCase()] || entity;
    });
    
    return text;
  };

  const getShareText = () => {
    const title = blog?.title || 'Check out this article';
    let excerpt = blog?.excerpt || '';
    const content = blog?.content || '';
    const author = blog?.author || 'AksharJobs';
    const category = blog?.category || '';
    
    // Clean the title (remove any HTML)
    const cleanTitle = cleanHtml(title);
    
    // If excerpt looks like it contains full HTML document, try to use content instead
    // or extract first paragraph from content
    if (excerpt && (excerpt.includes('<!DOCTYPE') || excerpt.includes('<html') || excerpt.length > 2000)) {
      // Excerpt seems to be corrupted with full HTML, try to extract from content
      if (content) {
        const cleanContent = cleanHtml(content);
        // Extract first meaningful paragraph (at least 50 chars)
        const firstParagraph = cleanContent.split(/\n\n|\.\s+/).find(p => p.trim().length > 50);
        if (firstParagraph) {
          excerpt = firstParagraph.trim();
        } else {
          // Fallback to first 200 chars of content
          excerpt = cleanContent.substring(0, 200).trim();
        }
      } else {
        excerpt = ''; // Skip excerpt if it's too corrupted
      }
    }
    
    // Create a professional and attractive share message
    // Use emojis on mobile devices, text-only on desktop for better compatibility
    const isMobile = isMobileDevice();
    const useEmojis = isMobile;
    
    // Title formatting - bold with optional emoji
    let shareText = useEmojis 
      ? `📖 *${cleanTitle}*\n\n` 
      : `*${cleanTitle}*\n\n`;
    
    if (excerpt) {
      // Clean HTML tags and content from excerpt
      let cleanExcerpt = cleanHtml(excerpt);
      
      // Remove the title if it appears in the excerpt (to avoid double heading)
      const titleLower = cleanTitle.toLowerCase().trim();
      const excerptLower = cleanExcerpt.toLowerCase();
      
      if (excerptLower.includes(titleLower)) {
        // Find and remove the title from excerpt
        const titleIndex = excerptLower.indexOf(titleLower);
        if (titleIndex !== -1) {
          // Remove title and any following punctuation/whitespace
          cleanExcerpt = cleanExcerpt.substring(titleIndex + cleanTitle.length).trim();
          // Remove leading colons, dashes, or other punctuation
          cleanExcerpt = cleanExcerpt.replace(/^[:-\s]+/, '').trim();
        }
      }
      
      // Remove any HTML document structure words that might have leaked through
      cleanExcerpt = cleanExcerpt.replace(/\b(DOCTYPE|html|head|body|meta|title|style|script|lang|charset|viewport|content|width|device-width|initial-scale)\b/gi, '');
      
      // Remove CSS property patterns but be careful not to remove too much
      // Only remove if it looks like CSS (has colon and semicolon pattern)
      cleanExcerpt = cleanExcerpt.replace(/[a-z-]+\s*:\s*[^;]+;(\s*[a-z-]+\s*:\s*[^;]+;)*/gi, '');
      
      // Remove CSS blocks in curly braces
      cleanExcerpt = cleanExcerpt.replace(/\{[^}]{0,200}\}/g, '');
      
      // Remove HTML tag-like patterns that might remain
      cleanExcerpt = cleanExcerpt.replace(/<\/?[a-z][^>]*>/gi, '');
      
      // Remove common HTML attributes that might have leaked
      cleanExcerpt = cleanExcerpt.replace(/\b(class|id|style|href|src|alt|title)\s*=\s*["'][^"']*["']/gi, '');
      
      // Final cleanup of whitespace and normalize
      cleanExcerpt = cleanExcerpt.replace(/\s+/g, ' ').trim();
      
      // Remove any remaining artifacts that look like code
      // Only if they're at the start and look like HTML/CSS
      if (cleanExcerpt.match(/^(DOCTYPE|html|<!|@media|function|var\s|const\s|let\s)/i)) {
        // Try to find the first actual sentence
        const sentenceMatch = cleanExcerpt.match(/[A-Z][^.!?]*[.!?]/);
        if (sentenceMatch) {
          cleanExcerpt = cleanExcerpt.substring(cleanExcerpt.indexOf(sentenceMatch[0]));
        }
      }
      
      // Limit excerpt to 250 characters for better readability
      const shortExcerpt = cleanExcerpt.length > 250 
        ? cleanExcerpt.substring(0, 250).trim() + '...' 
        : cleanExcerpt.trim();
      
      if (shortExcerpt && shortExcerpt.length > 20) { // Only add if meaningful content
        shareText += `"${shortExcerpt}"\n\n`;
      }
    }
    
    // Category/insights line with optional emoji
    if (category) {
      shareText += useEmojis 
        ? `✨ Discover more insights on ${category} and professional growth.\n\n`
        : `Discover more insights on ${category} and professional growth.\n\n`;
    } else {
      shareText += useEmojis
        ? `✨ Discover more insights on career development and professional growth.\n\n`
        : `Discover more insights on career development and professional growth.\n\n`;
    }
    
    // Author line with optional emoji
    shareText += useEmojis
      ? `👤 Written by: ${author}\n\n`
      : `Written by: ${author}\n\n`;
    
    // Link line with optional emoji
    shareText += useEmojis
      ? `🔗 Read the full article:`
      : `Read the full article:`;
    
    return shareText;
  };

  const handleShare = async (platform) => {
    const url = getShareUrl();
    const text = getShareText();
    const title = blog?.title || 'Blog Article';
    const cleanTitle = cleanHtml(title);
    const fullMessage = text + '\n\n' + url;

    switch (platform) {
      case 'whatsapp':
        // WhatsApp handles emojis better with proper encoding
        window.open(`https://wa.me/?text=${encodeURIComponent(fullMessage)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'twitter':
        // Twitter has character limit, so create a shorter version
        // Use emoji on mobile for Twitter too
        const isMobile = isMobileDevice();
        const twitterText = isMobile 
          ? `📖 ${cleanTitle}\n\n${url}`
          : `${cleanTitle}\n\n${url}`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'telegram':
        window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'email':
        // Email subject - use emoji on mobile
        const isMobileEmail = isMobileDevice();
        const emailSubject = isMobileEmail
          ? `📚 ${cleanTitle} - AksharJobs Blog`
          : `${cleanTitle} - AksharJobs Blog`;
        const emailBody = `Hi,\n\nI thought you might find this article interesting:\n\n${fullMessage}\n\nBest regards`;
        window.location.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(url).then(() => {
          alert('Link copied to clipboard!');
        }).catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = url;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          alert('Link copied to clipboard!');
        });
        break;
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({
              title: title,
              text: text,
              url: url
            });
          } catch (err) {
            if (err.name !== 'AbortError') {
              console.error('Error sharing:', err);
            }
          }
        }
        break;
      default:
        break;
    }
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="blog-page">
        <div className="blog-loading">
          <p>Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="blog-page">
        <button
          type="button"
          onClick={() => navigate('/blog')}
          className="blog-back-home-btn"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Blog
        </button>
        <div className="blog-no-results">
          <FontAwesomeIcon icon={faBook} className="no-results-icon" />
          <h3>{error || 'Blog post not found'}</h3>
          <button onClick={() => navigate('/blog')} className="btn-primary">
            Go to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      {/* Back to Blog Button */}
      <button
        type="button"
        onClick={() => navigate('/blog')}
        className="blog-back-home-btn"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        Back to Blog
      </button>

      {/* Blog Detail Content */}
      <article className="blog-detail-container">
        <div className="blog-detail-header">
          <div className="blog-detail-category">
            <FontAwesomeIcon icon={faTag} />
            <span>{blog.category || 'Uncategorized'}</span>
          </div>
          <h1 className="blog-detail-title">{blog.title}</h1>
          <div className="blog-detail-meta">
            <span className="blog-meta-item">
              <FontAwesomeIcon icon={faUser} />
              {blog.author || 'Unknown'}
            </span>
            <span className="blog-meta-item">
              <FontAwesomeIcon icon={faCalendar} />
              {formatDate(blog.date || blog.createdAt)}
            </span>
            <span className="blog-meta-item">
              <FontAwesomeIcon icon={faClock} />
              {blog.readTime || '5 min read'}
            </span>
          </div>
        </div>

        {blog.image && (
          <div className="blog-detail-image-wrapper">
            <img 
              src={getImageUrl(blog.image)} 
              alt={blog.title}
              className="blog-detail-image"
            />
          </div>
        )}

        <div className="blog-detail-content">
          {blog.content ? (
            <div 
              className="blog-detail-body"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          ) : (
            <div 
              className="blog-detail-body"
              dangerouslySetInnerHTML={{ __html: blog.excerpt || '<p>Content not available.</p>' }}
            />
          )}

          {blog.tags && Array.isArray(blog.tags) && blog.tags.length > 0 && (
            <div className="blog-detail-tags">
              <h3>Tags:</h3>
              <div className="blog-tags-list">
                {blog.tags.map((tag, idx) => (
                  <span key={idx} className="blog-tag">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Engagement Buttons */}
        <div className="blog-detail-engagement">
          <button 
            className={`blog-engagement-btn blog-like-btn ${isLiked ? 'liked' : ''}`}
            onClick={handleLike}
          >
            <FontAwesomeIcon icon={faHeart} />
            <span>{likes}</span>
          </button>
          <button 
            className="blog-engagement-btn blog-comment-btn"
            onClick={handleComment}
          >
            <FontAwesomeIcon icon={faComment} />
            <span>{comments}</span>
          </button>
          <div className="blog-share-wrapper">
            <button 
              className="blog-engagement-btn blog-share-btn"
              onClick={() => setShowShareMenu(!showShareMenu)}
            >
              <FontAwesomeIcon icon={faShare} />
              <span>Share</span>
            </button>
            {showShareMenu && (
              <div className="blog-share-menu">
                {navigator.share && (
                  <button 
                    className="blog-share-option"
                    onClick={() => handleShare('native')}
                  >
                    <span>📱</span> Share via...
                  </button>
                )}
                <button 
                  className="blog-share-option"
                  onClick={() => handleShare('whatsapp')}
                >
                  <span>💬</span> WhatsApp
                </button>
                <button 
                  className="blog-share-option"
                  onClick={() => handleShare('facebook')}
                >
                  <span>📘</span> Facebook
                </button>
                <button 
                  className="blog-share-option"
                  onClick={() => handleShare('twitter')}
                >
                  <span>🐦</span> Twitter
                </button>
                <button 
                  className="blog-share-option"
                  onClick={() => handleShare('linkedin')}
                >
                  <span>💼</span> LinkedIn
                </button>
                <button 
                  className="blog-share-option"
                  onClick={() => handleShare('telegram')}
                >
                  <span>✈️</span> Telegram
                </button>
                <button 
                  className="blog-share-option"
                  onClick={() => handleShare('email')}
                >
                  <span>📧</span> Email
                </button>
                <button 
                  className="blog-share-option"
                  onClick={() => handleShare('copy')}
                >
                  <span>📋</span> Copy Link
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="blog-detail-footer">
          <button 
            onClick={() => navigate('/blog')}
            className="btn-primary"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to All Articles
          </button>
        </div>
      </article>

      {/* Comment Dialog */}
      {showCommentDialog && (
        <div className="blog-comment-dialog-overlay" onClick={() => setShowCommentDialog(false)}>
          <div className="blog-comment-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="blog-comment-dialog-header">
              <h3>Add a Comment</h3>
              <button 
                className="blog-comment-dialog-close"
                onClick={() => setShowCommentDialog(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="blog-comment-dialog-body">
              <textarea
                className="blog-comment-input"
                placeholder="Write your comment here..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                rows={6}
              />
            </div>

            <div className="blog-comment-dialog-footer">
              <button
                className="blog-comment-cancel-btn"
                onClick={() => {
                  setShowCommentDialog(false);
                  setCommentText('');
                }}
              >
                Cancel
              </button>
              <button
                className="blog-comment-submit-btn"
                onClick={handleSubmitComment}
                disabled={submittingComment || !commentText.trim()}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                {submittingComment ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments List */}
      {commentList.length > 0 && (
        <div className="blog-comments-section">
          <h3 className="blog-comments-title">Comments ({comments})</h3>
          <div className="blog-comments-list">
            {commentList.map((comment, idx) => (
              <div key={comment.id || idx} className="blog-comment-item">
                <div className="blog-comment-header">
                  <span className="blog-comment-author">{comment.userName || 'Anonymous'}</span>
                  <span className="blog-comment-date">{formatCommentDate(comment.createdAt)}</span>
                </div>
                <div className="blog-comment-text">{comment.comment}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDetail;

